import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, FormControlLabel } from '@mui/material'
import { styled } from '@mui/material/styles'
import WhiteScrollingTable from './WhiteScrollingTable'

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(0.75),
  marginBottom: 0,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(3),
  },
}))

const TableContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isFirst',
})(({ theme, isFirst }) => ({
  marginTop: isFirst ? theme.spacing(3.75) : theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    marginLeft: -16,
    marginRight: -16,
    marginTop: theme.spacing(3.75),
  },
}))

const Label = styled('span')(({ theme }) => ({
  color: '#1A3967',
  fontSize: theme.typography.pxToRem(16),
}))

const CheckboxStyled = styled(Checkbox)(({ theme }) => ({
  color: '#B9B9B9 !important',
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}))

const BenchmarkLabel = React.memo(() => <Label>Show Benchmark Data</Label>)
BenchmarkLabel.displayName = 'BenchmarkLabel'

function BenchmarkScrollingTable({
  data = [],
  columns = [],
  presentation = {},
  className = '',
}) {
  const [checked, setChecked] = useState(false)

  const handleToggle = useCallback(() => {
    setChecked((prev) => !prev)
  }, [])

  const memoizedInputProps = useMemo(
    () => ({ 'aria-label': 'Show Benchmark Data Checkbox' }),
    []
  )

  // Memoize the control element
  const memoizedControl = useMemo(
    () => (
      <CheckboxStyled
        checked={checked}
        onChange={handleToggle}
        inputProps={memoizedInputProps}
      />
    ),
    [checked, handleToggle, memoizedInputProps]
  )

  const memoizedLabel = useMemo(() => <BenchmarkLabel />, [])

  return (
    <>
      <Container>
        <FormControlLabel control={memoizedControl} label={memoizedLabel} />
      </Container>

      {data.map((table, index) => {
        const tableRows = checked ? table.benchmarkData : table.data

        return (
          <TableContent key={index} isFirst={index === 0}>
            <WhiteScrollingTable
              columns={table.columns || columns}
              data={tableRows}
              presentation={presentation}
              otherClassName={className}
            />
          </TableContent>
        )
      })}
    </>
  )
}

BenchmarkScrollingTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      columns: PropTypes.array,
      data: PropTypes.array.isRequired,
      benchmarkData: PropTypes.array.isRequired,
    })
  ),
  columns: PropTypes.array,
  presentation: PropTypes.object,
  className: PropTypes.string,
}

export default React.memo(BenchmarkScrollingTable)
